import {LegacyWBIcon} from '@wandb/weave/common/components/elements/LegacyWBIcon';
import {LinkNoCrawl} from '@wandb/weave/common/util/links';
import {
  IconModel,
  IconRegistries,
  IconRocketLaunch,
  IconSettings,
} from '@wandb/weave/components/Icon';
import {ApolloQueryResult} from 'apollo-client';
import React, {FC, memo, useCallback, useMemo, useState} from 'react';
import {useLocation} from 'react-router';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Popup} from 'semantic-ui-react';

import defaultTeamAvatarPng from '../../assets/team-placeholder-large.png';
import {isTablet} from '../../breakpoints';
import EditableImage from '../../components/EditableImage';
import {useAccountOrganizationOrPersonalEntityUsingLaunch} from '../../components/Launch/launchActive';
import {useGetRoleOptions} from '../../components/MembersTable/TeamMembersTable/useGetRoleOptions';
import {TeamMemberRole} from '../../components/MembersTable/TeamMembersTable/utils';
import {TeamOrOrganizationRoleOption} from '../../components/MembersTable/TeamOrOrganizationRoleOption';
import {OrganizationMemberRole} from '../../components/MembersTable/util';
import {useIsGlobalRegistryEnabled} from '../../components/Registries/common/hooks';
import {ServiceAccountModal} from '../../components/ServiceAccountModal';
import useJoinTeam from '../../components/useJoinTeam';
import config from '../../config';
import {
  Maybe,
  useCreateInviteMutation,
  UserAccountType,
  useTeamOrganizationQuery,
  useUpdateEntityPhotoMutation,
} from '../../generated/graphql';
import {MemberData} from '../../graphql/teamQuery';
import {useViewer} from '../../state/viewer/hooks';
import {useAdminModeActive} from '../../util/admin';
import {doNotRetryContext, propagateErrorsContext} from '../../util/errors';
import {
  trackGlobalRegistryClicked,
  trackLaunchClicked,
  trackModelRegistryClicked,
} from '../../util/navigation';
import {checkIsServiceAccount} from '../../util/serviceAccounts';
import * as urls from '../../util/urls';
import {useModelsAccess} from '../../util/useModelsAccess';
import {ShareProfileTrigger} from '../EntityPage';
import * as S from './TeamSidebar.styles';
import {TeamPageQueryData} from './types';
import {useEntityUserStats} from './useEntityUserStats';
import {UserStatsSkeleton} from './UserStatsSkeleton';
import {useViewerMember} from './utils';

const MAX_TEAM_MEMBERS_SHOWN = 10;

type TeamSidebarProps = {
  members: MemberData[];
  teamData: TeamPageQueryData['entity'];
  tabRef?: React.RefObject<HTMLDivElement>; // For scrolling members tab into view on mobile when clicking Invite Team Members link
  refetch?: () => Promise<ApolloQueryResult<any>>;
};

const isOnPrem = config.ENVIRONMENT_IS_PRIVATE;

export const TeamSidebar: FC<TeamSidebarProps> = memo(
  ({members, teamData, tabRef, refetch}) => {
    const viewer = useViewer();
    const viewerMember = useViewerMember(teamData, viewer);
    const location = useLocation();
    const adminModeActive = useAdminModeActive();
    const [updateEntityPhoto] = useUpdateEntityPhotoMutation();

    const [createInviteMutation] = useCreateInviteMutation({
      context: {...propagateErrorsContext(), ...doNotRetryContext()},
    });
    const teamOrganization = useTeamOrganizationQuery({
      variables: {entityName: teamData.name},
    });

    const {hasModelsAccess} = useModelsAccess();

    const {isLaunchActive} =
      useAccountOrganizationOrPersonalEntityUsingLaunch();

    const canAnyMemberInviteTeamMembers =
      teamOrganization.data?.entity?.settings.membersCanInvite ?? false;
    const canViewerInviteTeamMembers =
      (canAnyMemberInviteTeamMembers &&
        viewerMember?.role === TeamMemberRole.MEMBER) ||
      viewerMember?.role === TeamMemberRole.ADMIN;

    const [avatarFailedToLoadSet, setAvatarFailedToLoadSet] = useState<
      Set<string>
    >(new Set());
    const registerAvatarLoadFailure = useCallback(
      (userID: Maybe<string> | undefined) => {
        if (userID == null || avatarFailedToLoadSet.has(userID)) {
          return;
        }
        setAvatarFailedToLoadSet(prev => new Set([...prev, userID]));
      },
      [avatarFailedToLoadSet]
    );

    const {data: entityUserStats, loading: isEntityUserStatsLoading} =
      useEntityUserStats(teamData.name, avatarFailedToLoadSet);

    const shownMembers = useMemo(
      () =>
        members
          .map(m => ({
            ...m,
            showFallbackAvatar:
              !m.pending &&
              (!m.photoUrl ||
                (m.id != null && avatarFailedToLoadSet.has(m.id))),
          }))
          .slice(0, MAX_TEAM_MEMBERS_SHOWN),
      [members, avatarFailedToLoadSet]
    );

    const membersAreTruncated = members.length !== shownMembers.length;
    const teamSettingsURL = urls.teamSettings(teamData.name);

    const org = teamOrganization.data?.entity?.organization;
    const viewerIsOrgMember =
      org?.members.some(member => member.id === viewer?.id) ?? false;

    const viewerIsOrgMemberOrWBAdmin = viewerIsOrgMember || adminModeActive;

    const showOrgLink = !isOnPrem && viewerIsOrgMemberOrWBAdmin;

    const isViewerTeamMember = viewerMember != null;
    const isViewerTeamAdmin = viewerMember?.role === TeamMemberRole.ADMIN;
    const isViewerTeamMemberOrWBAdmin = isViewerTeamMember || adminModeActive;
    const isViewerTeamAdminOrWBAdmin = isViewerTeamAdmin || adminModeActive;
    const isViewerOrganizationAdmin =
      org?.members.some(
        member =>
          member.id === viewer?.id &&
          member.role === OrganizationMemberRole.ADMIN
      ) ?? false;
    const isViewerOrganizationAdminNotTeamMember =
      viewerMember == null && isViewerOrganizationAdmin;
    const isViewerOrganizationAdminTeamMember =
      isViewerOrganizationAdmin &&
      viewerMember &&
      viewerMember.role !== TeamMemberRole.ADMIN;
    const instanceAdminTeamAccessDisabled =
      viewer?.instanceAdminTeamAccessDisabled;

    const showLaunch = isLaunchActive && isViewerTeamMemberOrWBAdmin;

    const isTeamPageReadOnly = teamData?.readOnly ?? false;
    const numTeamProjects = teamData?.projects?.edges?.length ?? 0;
    const showTeamSettingsLink =
      isViewerTeamAdminOrWBAdmin && numTeamProjects === 0;

    const showingQuickstart = !isTeamPageReadOnly && numTeamProjects === 0;

    const isMembersUrlEnabled = !showingQuickstart
      ? isViewerTeamMemberOrWBAdmin
      : showTeamSettingsLink;

    const teamMembersUrl = urls.teamMembers(teamData.name);

    const nonServiceMemberCount = members.filter(member => {
      return !checkIsServiceAccount(member.accountType);
    }).length;

    const {roleOptions} = useGetRoleOptions(teamData.name);

    const {handleJoinTeamAs} = useJoinTeam(createInviteMutation, refetch);

    const {enableGlobalRegistry} = useIsGlobalRegistryEnabled();

    const teamSettingsIconText = (
      <S.CenteredIconText>
        <IconSettings />
        Team settings
      </S.CenteredIconText>
    );
    const inviteMemberIconText = (
      <S.CenteredIconText>
        <LegacyWBIcon name="plus" />
        Invite team members
      </S.CenteredIconText>
    );

    return (
      <div style={{padding: '0px 20px 20px 20px'}}>
        <EditableImage
          placeholderText="Add a team logo"
          photoUrl={teamData.photoUrl ?? defaultTeamAvatarPng}
          displaySize={200}
          label={false}
          readOnly={teamData.readOnly}
          entityName={teamData.name}
          save={newVal => {
            updateEntityPhoto({
              variables: {
                photoUrl: newVal,
                entityName: teamData.name,
              },
            });
          }}
        />
        <S.TeamSidebarHeader as="h1">
          {teamData.name}
          <ShareProfileTrigger entityName={teamData.name} fontSize={28} />
        </S.TeamSidebarHeader>
        {isViewerTeamAdminOrWBAdmin || isViewerOrganizationAdminTeamMember ? (
          <S.TeamSidebarLink
            data-test="team-settings-link"
            to={teamSettingsURL}>
            {teamSettingsIconText}
          </S.TeamSidebarLink>
        ) : isViewerTeamMemberOrWBAdmin ? (
          <Popup
            data-test="team-settings-popup"
            inverted
            size="mini"
            position="top center"
            content="Please ask a team admin to make changes."
            trigger={
              <S.TeamSidebarDisabledLinkText data-test="team-settings-link">
                {teamSettingsIconText}
              </S.TeamSidebarDisabledLinkText>
            }
          />
        ) : null}
        {isViewerTeamMemberOrWBAdmin &&
          hasModelsAccess &&
          (enableGlobalRegistry ? (
            <S.TeamSidebarApplicationsLink>
              <LinkNoCrawl
                onClick={() => trackGlobalRegistryClicked('Team sidebar')}
                to={urls.registries()}
                className="see-all-link">
                <S.CenteredIconText>
                  <IconRegistries />
                  Registry
                </S.CenteredIconText>
              </LinkNoCrawl>
            </S.TeamSidebarApplicationsLink>
          ) : (
            <S.TeamSidebarApplicationsLink>
              <LinkNoCrawl
                data-test="model-registry"
                onClick={() => trackModelRegistryClicked('Team sidebar')}
                to={urls.objectRegistry('model', undefined, teamData.name)}
                className="see-all-link">
                <S.CenteredIconText>
                  <IconModel />
                  Model Registry
                </S.CenteredIconText>
              </LinkNoCrawl>
            </S.TeamSidebarApplicationsLink>
          ))}
        {showLaunch && (
          <S.TeamSidebarApplicationsLink>
            <LinkNoCrawl
              data-test="launch"
              onClick={() => trackLaunchClicked('Team sidebar')}
              to={urls.launchOverview()}
              className="see-all-link">
              <S.CenteredIconText>
                <IconRocketLaunch />
                Launch
              </S.CenteredIconText>
            </LinkNoCrawl>
          </S.TeamSidebarApplicationsLink>
        )}
        {(isEntityUserStatsLoading || entityUserStats.length > 0) && (
          <S.Section>
            <S.ActiveUsersHeader>
              <span>Weekly most active</span>
              <span>Runs</span>
            </S.ActiveUsersHeader>
            {isEntityUserStatsLoading ? (
              <UserStatsSkeleton numUsers={10} />
            ) : (
              entityUserStats.map(
                ({
                  id,
                  username,
                  name,
                  photoUrl,
                  accountType,
                  runCount,
                  showFallbackAvatar,
                }) => {
                  return (
                    <S.ActiveUserRow key={id}>
                      <S.ActiveUserName>
                        {showFallbackAvatar || !photoUrl ? (
                          <S.FallbackAvatar $topOffset={-1}>
                            {getFirstLetterCapitalized(username ?? name)}
                          </S.FallbackAvatar>
                        ) : (
                          <S.TeamMemberAvatar
                            src={photoUrl}
                            onError={() => registerAvatarLoadFailure(id)}
                          />
                        )}
                        {accountType === UserAccountType.User ? (
                          <LinkNoCrawl to={urls.profilePage(username)}>
                            {username}
                          </LinkNoCrawl>
                        ) : checkIsServiceAccount(accountType) ? (
                          <ServiceAccountModal
                            serviceAccountName={name}
                            teamMembersUrl={
                              isTeamPageReadOnly ? undefined : teamMembersUrl
                            }
                          />
                        ) : (
                          username
                        )}
                      </S.ActiveUserName>
                      <S.ActiveUserRunCount>{runCount}</S.ActiveUserRunCount>
                    </S.ActiveUserRow>
                  );
                }
              )
            )}
          </S.Section>
        )}
        {isViewerOrganizationAdminNotTeamMember &&
          !instanceAdminTeamAccessDisabled && (
            <S.JoinTeamDropdown
              options={roleOptions.map(roleOption => {
                return {
                  text: (
                    <TeamOrOrganizationRoleOption roleOption={roleOption} />
                  ),
                  value: roleOption.value,
                  onClick: () => {
                    const isRoleEnabledForPlan =
                      (roleOption.isPremiumOption &&
                        !roleOption.isPremiumFeatureDisabled) ||
                      !roleOption.isPremiumOption;
                    if (isRoleEnabledForPlan) {
                      handleJoinTeamAs(
                        teamData.name,
                        viewer?.email,
                        roleOption
                      );
                    }
                  },
                };
              })}
              trigger={
                <S.JoinTeamDropdownMenu>+ Join Team</S.JoinTeamDropdownMenu>
              }
            />
          )}
        <S.Section>
          <S.TeamMembersHeader>
            {isMembersUrlEnabled ? (
              <LinkNoCrawl data-test="members-header-link" to={teamMembersUrl}>
                Members
                {nonServiceMemberCount > 0 && ` (${nonServiceMemberCount})`}
              </LinkNoCrawl>
            ) : (
              <S.TeamMembersHeaderText data-test="members-header-text">
                Members
                {nonServiceMemberCount > 0 && ` (${nonServiceMemberCount})`}
              </S.TeamMembersHeaderText>
            )}
          </S.TeamMembersHeader>
          {canViewerInviteTeamMembers ? (
            <S.TeamSidebarLink
              onClick={() => {
                if (isTablet() && location.pathname.endsWith('/members')) {
                  // Scroll to members section if we're already on members page
                  tabRef?.current?.scrollIntoView?.({
                    behavior: 'smooth',
                  });
                }
              }}
              data-test="invite-team-member-link"
              to={teamMembersUrl}>
              {inviteMemberIconText}
            </S.TeamSidebarLink>
          ) : isViewerTeamMemberOrWBAdmin ? (
            <Popup
              data-test="invite-team-member-popup"
              inverted
              size="mini"
              position="top center"
              content="Please ask a team admin to invite team members."
              trigger={
                <S.TeamSidebarDisabledLinkText data-test="invite-team-member-link">
                  {inviteMemberIconText}
                </S.TeamSidebarDisabledLinkText>
              }
            />
          ) : null}
          {shownMembers.length > 0 ? (
            shownMembers.map(
              ({
                id,
                email,
                username,
                name,
                photoUrl,
                pending,
                accountType,
                showFallbackAvatar,
              }) => (
                <S.TeamMemberRow key={id}>
                  {showFallbackAvatar ? (
                    <S.FallbackAvatar>
                      {getFirstLetterCapitalized(username ?? name ?? email)}
                    </S.FallbackAvatar>
                  ) : (
                    photoUrl && (
                      <S.TeamMemberAvatar
                        src={photoUrl}
                        onError={() => registerAvatarLoadFailure(id)}
                      />
                    )
                  )}
                  <S.TeamMemberName>
                    {accountType === UserAccountType.User && username ? (
                      <LinkNoCrawl to={urls.profilePage(username)}>
                        {username}
                      </LinkNoCrawl>
                    ) : checkIsServiceAccount(accountType) && name ? (
                      <ServiceAccountModal
                        serviceAccountName={name}
                        teamMembersUrl={
                          isTeamPageReadOnly ? undefined : teamMembersUrl
                        }
                      />
                    ) : (
                      email
                    )}
                  </S.TeamMemberName>
                  {pending && <S.TeamMemberTag>Invited</S.TeamMemberTag>}
                </S.TeamMemberRow>
              )
            )
          ) : (
            <S.NoMembersMessage>
              This team has no public members. You must be a member to see who's
              a part of this team.
            </S.NoMembersMessage>
          )}
          {membersAreTruncated && isMembersUrlEnabled && (
            <S.TeamMembersViewAllLink>
              <LinkNoCrawl to={teamMembersUrl}>
                <LegacyWBIcon name="move" />
                View all
              </LinkNoCrawl>
            </S.TeamMembersViewAllLink>
          )}
        </S.Section>
        {showOrgLink && org != null && (
          <S.Section>
            <S.TeamSidebarLink to={urls.subscriptions()}>
              <S.CenteredIconText>
                <LegacyWBIcon name="configuration" />
                <S.SubscriptionsText>{org.name}</S.SubscriptionsText>
              </S.CenteredIconText>
            </S.TeamSidebarLink>
          </S.Section>
        )}
      </div>
    );
  }
);
TeamSidebar.displayName = 'TeamSidebar';

function getFirstLetterCapitalized(str: Maybe<string> | undefined): string {
  if (!str) {
    return '';
  }
  return str.charAt(0).toUpperCase();
}
